import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Select,
  message,
  InputNumber,
  Input,
  Modal,
  Spin,
  Typography,
} from "antd";
import {
  ShoppingOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  PrinterOutlined,
  UserOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleOutlined,
  CarOutlined,
  InboxOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import axios from "axios";

import {
  selectIsOrderLoaded,
  selectIsOrderLoading,
  selectOrder,
  selectOrderItems,
} from "../../../redux-toolkit/order/order-selector";
import {
  fetchOrderById,
  fetchOrderItemsByOrderId,
} from "../../../redux-toolkit/order/order-thunks";
import { resetOrderState } from "../../../redux-toolkit/order/order-slice";
import { OrderItemResponse } from "../../../types/types";
import { BASE_URL } from "../../../constants/urlConstants";
import { selectUserFromUserState } from "../../../redux-toolkit/user/user-selector";
import FinllieLoader from "../../logo/FinllieLoader";

import "./ManageUserOrder.css";

const { Option }   = Select;
const { TextArea } = Input;

/* ── Statuses after which nothing can be changed ── */
const LOCKED_STATUSES = new Set(["DELIVERED", "CANCELLED", "FULFILLED"]);

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  REQUESTED:  { label: "Requested",  color: "#f57f17", bg: "#fffde7", icon: <ClockCircleOutlined /> },
  ORDERED:    { label: "Ordered",    color: "#e65100", bg: "#fff3e0", icon: <ShoppingOutlined /> },
  PENDING:    { label: "Pending",    color: "#e65100", bg: "#fff3e0", icon: <ClockCircleOutlined /> },
  CONFIRMED:  { label: "Confirmed",  color: "#1565c0", bg: "#e3f2fd", icon: <CheckCircleOutlined /> },
  PROCESSING: { label: "Processing", color: "#6a1b9a", bg: "#f3e5f5", icon: <ClockCircleOutlined /> },
  SHIPPED:    { label: "Shipped",    color: "#00695c", bg: "#e0f2f1", icon: <CarOutlined /> },
  DELIVERED:  { label: "Delivered",  color: "#2e7d32", bg: "#e8f5e9", icon: <CheckCircleFilled /> },
  FULFILLED:  { label: "Fulfilled",  color: "#2e7d32", bg: "#e8f5e9", icon: <CheckCircleFilled /> },
  CANCELLED:  { label: "Cancelled",  color: "#c62828", bg: "#ffebee", icon: <CloseCircleFilled /> },
  RETURNED:   { label: "Returned",   color: "#4e342e", bg: "#efebe9", icon: <RollbackOutlined /> },
};

const getStatus = (s: string) =>
  STATUS_CONFIG[s?.toUpperCase()] ?? { label: s ?? "Unknown", color: "#555", bg: "#f5f5f5", icon: <InfoCircleOutlined /> };

const InfoRow: FC<{ icon: React.ReactNode; label: string; value?: string | number | null }> = ({ icon, label, value }) => (
  <div className="muo-info-row">
    <span className="muo-info-icon">{icon}</span>
    <span className="muo-info-label">{label}</span>
    <span className="muo-info-value">{value != null && String(value).trim() ? String(value) : "—"}</span>
  </div>
);

const ManageUserOrder: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const history  = useHistory();
  const params   = useParams<{ id: string }>();

  const order          = useSelector(selectOrder);
  const orderItems     = useSelector(selectOrderItems);
  const isOrderLoading = useSelector(selectIsOrderLoading);
  const isOrderLoaded  = useSelector(selectIsOrderLoaded);
  const user           = useSelector(selectUserFromUserState);

  const {
    id, email, firstName, lastName, totalPrice,
    postIndex, phoneNumber, date, city, address,
    paymentType, transactionId, deliveryStatus, orderType, createdAt,
  } = order;

  const [status,      setStatus]      = useState<string>(deliveryStatus || "ORDERED");
  const [isUpdating,  setIsUpdating]  = useState(false);
  const [isFulfilling, setIsFulfilling] = useState(false);  // progress for fulfill
  const [remarks,     setRemarks]     = useState("");

  const isAdmin         = user?.roles?.includes("ADMIN");
  const [isDistributor, setIsDistributor] = useState(false);
  const isDistReplenish = orderType === "DIST_REPLENISH";

  // Is the order in a terminal state — nothing editable
  const isLocked = LOCKED_STATUSES.has((deliveryStatus ?? "").toUpperCase());

  useEffect(() => { setIsDistributor(localStorage.getItem("role") === "3"); }, []);

  useEffect(() => {
    dispatch(fetchOrderById(params.id));
    return () => { dispatch(resetOrderState()); };
  }, [dispatch, params.id]);

  useEffect(() => {
    if (isOrderLoaded) {
      dispatch(fetchOrderItemsByOrderId(params.id));
      setStatus(order.deliveryStatus || "ORDERED");
    }
  }, [isOrderLoaded, order]);

  /* ── Auto-compute fulfilledQty = each item's ordered qty ── */
  const fulfilledQty = React.useMemo(() => {
    const map: Record<number, number> = {};
    (orderItems ?? []).forEach((item: OrderItemResponse) => {
      if (item.perfume?.id != null) {
        map[item.perfume.id] = item.quantity ?? 0;
      }
    });
    return map;
  }, [orderItems]);

  const handleStatusUpdate = async () => {
    if (!isDistributor && !isAdmin) {
      message.warning("Only admin/distributor can update order status.");
      return;
    }
    setIsUpdating(true);
    try {
      const res = await fetch(`${BASE_URL}/api/v1/order/${id}/status?status=${status}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!res.ok) throw new Error("Failed");
      message.success(`Status updated to "${status}"`);
      dispatch(fetchOrderById(params.id));
    } catch {
      message.error("Error updating status");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFulfillOrder = async () => {
    setIsFulfilling(true);
    try {
      const res = await fetch(`${BASE_URL}/api/v1/hq/orders/${id}/fulfill`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ fulfilledQty, remarks }),
      });

      let data;
      const contentType = res.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        data = await res.json();
      } else {
        data = await res.text();
      }

      if (!res.ok) {
        Modal.error({
          title: "Error",
          content: data?.message || data || "Something went wrong",
        });
        return;
      }

      message.success("Order fulfilled successfully");
      dispatch(fetchOrderById(params.id));
    } catch (err) {
      console.error(err);
      Modal.error({
        title: "Error",
        content: "Network error. Please try again.",
      });
    } finally {
      setIsFulfilling(false);
    }
  };

  if (isOrderLoading) {
    return (
      <div className="muo-page">
        <div className="muo-center"><Spin size="large" /></div>
      </div>
    );
  }

  const statusCfg = getStatus(deliveryStatus ?? "");
  const fullName  = [firstName, lastName].filter(Boolean).join(" ") || "—";
  const initials  = ((firstName?.[0] ?? "") + (lastName?.[0] ?? "")).toUpperCase() || "?";

  return (
    <>
      {/* ── Full-screen progress overlay while fulfilling ── */}
      {isFulfilling && <FinllieLoader text="Processing fulfillment..." />}

      <div className="muo-page">

        {/* ── HEADER ── */}
        <div className="muo-header">
          <button className="muo-back-btn" onClick={() => history.goBack()}>
            <ArrowLeftOutlined />
          </button>
          <div className="muo-header-text">
            <span className="muo-header-title">Order #{id}</span>
            <span className="muo-header-date">
              {createdAt
                ? new Date(createdAt).toLocaleString("en-IN", {
                    timeZone: "Asia/Kolkata",
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit", hour12: false,
                  })
                : "—"}
            </span>
          </div>
          <span className="muo-status-pill" style={{ color: statusCfg.color, background: statusCfg.bg }}>
            {statusCfg.icon} {statusCfg.label}
          </span>
          <button
            className="muo-invoice-btn"
            onClick={() => history.push(`/account/user/orders/${id}/invoice`)}
            title="Download Invoice"
          >
            <PrinterOutlined /> Invoice
          </button>
        </div>

        {/* ── LOCKED BANNER ── */}
        {isLocked && (
          <div className="muo-locked-banner">
            <CheckCircleFilled style={{ marginRight: 8 }} />
            This order is <strong>{statusCfg.label}</strong> — no further changes can be made.
          </div>
        )}

        {/* ── CUSTOMER HERO ── */}
        <div className="muo-hero">
          <div className="muo-avatar">{initials}</div>
          <div className="muo-hero-info">
            <div className="muo-hero-name">{fullName}</div>
            <div className="muo-hero-email">{email ?? "—"}</div>
            {orderType && (
              <span className="muo-order-type-badge">
                {orderType === "DIST_REPLENISH" ? "Distributor Replenishment" : "Retail Order"}
              </span>
            )}
          </div>
        </div>

        <div className="muo-body">

          {/* ── LEFT COLUMN ── */}
          <div className="muo-left">

            {/* Contact & Address */}
            <div className="muo-section">
              <div className="muo-section-title"><UserOutlined /> Contact & Delivery</div>
              <InfoRow icon={<PhoneOutlined />}       label="Phone"      value={phoneNumber} />
              <InfoRow icon={<EnvironmentOutlined />} label="City"       value={city} />
              <InfoRow icon={<EnvironmentOutlined />} label="Address"    value={address} />
              <InfoRow icon={<EnvironmentOutlined />} label="Post Index" value={postIndex} />
            </div>

            {/* Payment */}
            <div className="muo-section">
              <div className="muo-section-title"><CreditCardOutlined /> Payment</div>
              <InfoRow icon={<CreditCardOutlined />} label="Method"         value={paymentType} />
              <InfoRow icon={<InfoCircleOutlined />} label="Transaction ID"  value={transactionId} />
            </div>

            {/* Status update — hidden when locked or DIST_REPLENISH */}
            {(isAdmin || isDistributor) && !isDistReplenish && !isLocked && (
              <div className="muo-section">
                <div className="muo-section-title"><ShoppingOutlined /> Update Status</div>
                <div className="muo-status-update">
                  <Select
                    value={status}
                    onChange={setStatus}
                    className="muo-status-select"
                    size="middle"
                  >
                    {["ORDERED", "PROCESSING", "SHIPPED", "DELIVERED", "CANCELLED"].map((s) => (
                      <Option key={s} value={s}>{s}</Option>
                    ))}
                  </Select>
                  <button
                    className="muo-update-btn"
                    onClick={handleStatusUpdate}
                    disabled={isUpdating}
                  >
                    {isUpdating ? "Saving…" : "Update"}
                  </button>
                </div>
              </div>
            )}

            {/* HQ Fulfill block — hidden when locked */}
            {isAdmin && isDistReplenish && !isLocked && (
              <div className="muo-section">
                <div className="muo-section-title"><CheckCircleOutlined /> HQ Fulfillment</div>
                <TextArea
                  rows={2}
                  placeholder="Remarks (optional)"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="muo-remarks"
                />
                <button
                  className="muo-fulfill-btn"
                  onClick={handleFulfillOrder}
                  disabled={isFulfilling}
                >
                  <CheckCircleOutlined /> Accept Distributor Order
                </button>
              </div>
            )}
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="muo-right">

            {/* Summary strip */}
            <div className="muo-summary-strip">
              <div className="muo-summary-stat">
                <span className="muo-summary-val">
                  {totalPrice != null ? `₹${Number(totalPrice).toLocaleString()}` : "N/A"}
                </span>
                <span className="muo-summary-label">Total</span>
              </div>
              <div className="muo-summary-divider" />
              <div className="muo-summary-stat">
                <span className="muo-summary-val">{orderItems?.length ?? "—"}</span>
                <span className="muo-summary-label">Items</span>
              </div>
              <div className="muo-summary-divider" />
              <div className="muo-summary-stat">
                <span className="muo-summary-val">{paymentType ?? "—"}</span>
                <span className="muo-summary-label">Payment</span>
              </div>
            </div>

            {/* Order items */}
            <div className="muo-section muo-items-section">
              <div className="muo-section-title"><InboxOutlined /> Order Items</div>

              {orderItems?.length === 0 && (
                <div className="muo-no-items">No items found</div>
              )}

              {orderItems?.map((item: OrderItemResponse) => (
                <div key={item.id} className="muo-item">
                  <div className="muo-item-img-wrap">
                    <img
                      src={item.perfume?.filename || "/no-image.png"}
                      alt={item.perfume?.perfumeTitle}
                      className="muo-item-img"
                    />
                  </div>
                  <div className="muo-item-info">
                    <div className="muo-item-title">{item.perfume?.perfumeTitle ?? "—"}</div>
                    <div className="muo-item-brand">{item.perfume?.brand ?? ""}</div>
                    <div className="muo-item-meta">
                      <span className="muo-item-qty">Qty: {item.quantity}</span>
                      <span className="muo-item-price">₹{item.perfume?.price}</span>
                    </div>
                  </div>

                  {/* Fulfill qty — read-only, always mirrors ordered qty */}
                  {isAdmin && isDistReplenish && !isLocked && (
                    <div className="muo-fulfill-qty">
                      <span className="muo-fulfill-qty-label">Fulfill</span>
                      <div className="muo-fulfill-qty-value">
                        {fulfilledQty[item.perfume?.id] ?? item.quantity ?? 0}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageUserOrder;